import { z } from 'zod';
import type { MESSAGE_CHANNELS } from './audience.ts';
import { CardRemovalTriggers } from './card-delivery.ts';
import { TemplateSource } from './content-templates.ts';

/**
 * What a campaign sends (BB-98, CMP-002, AN-004): the delivery channels, up to eight variants with their share and
 * content for every channel, and a no-message control. One campaign may combine channels (VCG «Signal New»: Android,
 * iOS and Web push). Channel content belongs to the channel tickets (cards BB-61/BB-62, push BB-73, IAM BB-85);
 * here it is checked for presence only.
 */
export const DELIVERY_CHANNELS = ['push_android', 'push_ios', 'push_web', 'content_card', 'in_app', 'webhook'] as const;
export type DeliveryChannel = (typeof DELIVERY_CHANNELS)[number];

/** The reporting channel and platform of each delivery channel (facts, campaign `channels`). */
export const DELIVERY_CHANNEL_INFO: Record<DeliveryChannel, { channel: (typeof MESSAGE_CHANNELS)[number]; platform: 'android' | 'ios' | 'web' | null }> = {
  push_android: { channel: 'push', platform: 'android' },
  push_ios: { channel: 'push', platform: 'ios' },
  push_web: { channel: 'push', platform: 'web' },
  content_card: { channel: 'content_card', platform: null },
  in_app: { channel: 'in_app', platform: null },
  webhook: { channel: 'webhook', platform: null },
};

export const MAX_VARIANTS = 8;
export const CONTROL_VARIANT_API_ID = 'control';

const Percent = z.number().min(0).max(100).multipleOf(0.01);
const VariantApiId = z
  .string()
  .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/, 'lowercase letters, digits, _ and -')
  .refine((id) => id !== CONTROL_VARIANT_API_ID, 'control is reserved');

export const MessageVariant = z.strictObject({
  apiId: VariantApiId,
  name: z.string().trim().min(1).max(100),
  percent: Percent,
  /** Content per delivery channel of the campaign. */
  content: z.partialRecord(z.enum(DELIVERY_CHANNELS), z.record(z.string(), z.unknown())),
  /** The template version a channel's content was copied from (BB-56); the content itself never follows the template. */
  sourceTemplates: z.partialRecord(z.enum(DELIVERY_CHANNELS), TemplateSource).optional(),
});
export type MessageVariant = z.infer<typeof MessageVariant>;

export const CampaignMessages = z
  .strictObject({
    schemaVersion: z.literal(1),
    channels: z.array(z.enum(DELIVERY_CHANNELS)).min(1).max(DELIVERY_CHANNELS.length),
    variants: z.array(MessageVariant).min(1).max(MAX_VARIANTS),
    /** Share of recipients that get nothing (a card campaign's control still logs an impression, BB-70). */
    control: z.strictObject({ percent: Percent }).default({ percent: 0 }),
  })
  .superRefine((m, ctx) => {
    if (new Set(m.channels).size !== m.channels.length) ctx.addIssue({ code: 'custom', path: ['channels'], message: 'a channel is listed twice' });
    if (new Set(m.variants.map((v) => v.apiId)).size !== m.variants.length) ctx.addIssue({ code: 'custom', path: ['variants'], message: 'variant ids must be unique' });
    const total = Math.round((m.variants.reduce((s, v) => s + v.percent, 0) + m.control.percent) * 100);
    if (total !== 10_000) ctx.addIssue({ code: 'custom', path: ['variants'], message: 'variant and control shares must add up to 100%' });
    m.variants.forEach((v, i) => {
      for (const channel of m.channels) if (!v.content[channel]) ctx.addIssue({ code: 'custom', path: ['variants', i, 'content', channel], message: `content for ${channel} is missing` });
      for (const channel of Object.keys(v.content)) if (!m.channels.includes(channel as DeliveryChannel)) ctx.addIssue({ code: 'custom', path: ['variants', i, 'content', channel], message: `${channel} is not a channel of the campaign` });
    });
  });
export type CampaignMessages = z.infer<typeof CampaignMessages>;

/** Reporting channels a set of delivery channels uses (must be among the campaign's `channels`). */
export const reportingChannelsOf = (channels: readonly DeliveryChannel[]): Array<(typeof MESSAGE_CHANNELS)[number]> => [...new Set(channels.map((c) => DELIVERY_CHANNEL_INFO[c].channel))];

/**
 * Content of the `content_card` channel of a variant (BB-63/BB-64): the card before templating (`CardContent` of
 * BB-61, checked by the card composer BB-62), when it is created and how long it lives (default 30 days, Braze's max),
 * and the events that withdraw it again (`removalTriggers`, BB-65).
 */
export const ContentCardChannelContent = z.looseObject({
  card: z.record(z.string(), z.unknown()),
  removalTriggers: CardRemovalTriggers.optional(),
  creationMode: z.enum(['first_impression', 'step_entry']).default('first_impression'),
  expiry: z
    .discriminatedUnion('mode', [z.strictObject({ mode: z.literal('duration'), seconds: z.number().int().min(60).max(30 * 86_400) }), z.strictObject({ mode: z.literal('at'), at: z.iso.datetime() })])
    .default({ mode: 'duration', seconds: 30 * 86_400 }),
});
export type ContentCardChannelContent = z.infer<typeof ContentCardChannelContent>;

/** Outcome of one dispatch (one recipient of one trigger); `deferred` — held back by a rate limit or quiet hours, the same dispatch runs again later (BB-102). */
export const DISPATCH_STATUSES = ['delivered', 'partial', 'suppressed', 'control', 'unreachable', 'aborted', 'failed', 'deferred'] as const;
export type DispatchStatus = (typeof DISPATCH_STATUSES)[number];
